import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Divider,
  Fade,
  Zoom,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  LocationOn as LocationOnIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  AccessTime as AccessTimeIcon,
  Send as SendIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import GeometricBackground from '../components/GeometricBackground';
import Seo from '../components/Seo';

const Contact = () => {
  const { businessName, businessPhone, businessEmail, businessAddress } = useSelector((state) => state.configs);
  const { workingHours } = useSelector((state) => state.template);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(formData.subject || `Website enquiry from ${formData.name}`);
    const body = encodeURIComponent(`${formData.message}\n\nFrom: ${formData.name} <${formData.email}>${formData.phone ? `\nPhone: ${formData.phone}` : ''}`);
    window.location.href = `mailto:${businessEmail}?subject=${subject}&body=${body}`;
  };

  const contactInfo = [
    ...(businessAddress ? [{
      icon: <LocationOnIcon sx={{ fontSize: 24 }} />,
      title: 'Visit Our Store',
      details: [businessAddress],
      color: 'var(--primary)',
    }] : []),
    ...((workingHours?.enabled && workingHours?.lines?.length > 0) ? [{
      icon: <AccessTimeIcon sx={{ fontSize: 24 }} />,
      title: 'Working Hours',
      details: workingHours.lines.map((l) => (l.label ? `${l.label}: ${l.value}` : l.value)),
      color: 'var(--primary)',
    }] : []),
    ...(businessPhone ? [{
      icon: <PhoneIcon sx={{ fontSize: 24 }} />,
      title: 'Phone',
      details: [businessPhone],
      color: '#333333',
      isLink: true,
      linkPrefix: 'tel:',
    }] : []),
    ...(businessEmail ? [{
      icon: <EmailIcon sx={{ fontSize: 24 }} />,
      title: 'Email',
      details: [businessEmail],
      color: '#333333',
      isLink: true,
      linkPrefix: 'mailto:',
    }] : []),
  ];

  return (
    <GeometricBackground minHeight="100vh">
      <Seo title="Contact Us" description="Get in touch with us — location, phone, email and working hours." />
      <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 5, md: 6 } }}>
        {/* Enhanced Header */}
        <Fade in={true} timeout={800}>
          <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 5, md: 6 } }}>
            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 700, 
                mb: 2,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                background: 'linear-gradient(135deg, var(--primary) 0%, var(--button-hover) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                opacity: 0.9,
                letterSpacing: '-0.5px',
              }}
            >
              Get in Touch
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#666', 
                maxWidth: 600, 
                mx: 'auto',
                fontSize: { xs: '0.95rem', sm: '1.1rem' },
                lineHeight: 1.7,
                fontWeight: 400,
              }}
            >
              We're here to help! Reach out to us for any questions about our products, orders, or visit our store.
            </Typography>
          </Box>
        </Fade>

        <Grid container spacing={{ xs: 3, sm: 4 }} sx={{ alignItems: 'stretch' }}>
          {/* Enhanced Contact Form */}
          {businessEmail && (
          <Grid item xs={12} lg={6} sx={{ display: 'flex' }}>
            <Zoom in={true} timeout={1000}>
              <Paper
                elevation={0}
                sx={{ 
                  p: { xs: 3, sm: 4, md: 5 },
                  background: 'white',
                  borderRadius: '12px',
                  border: '1px solid rgba(0,0,0,0.08)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                  transition: 'all 0.3s ease',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box sx={{
                    p: 1.5,
                    borderRadius: '10px',
                    background: 'color-mix(in srgb, var(--primary) 8%, transparent)',
                    mr: 2,
                  }}>
                    <SendIcon sx={{ fontSize: 28, color: 'var(--primary)', opacity: 0.8 }} />
                  </Box>
                  <Box>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 700, 
                      fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                      color: '#1a1a1a'
                    }}>
                      Send Us a Message
                    </Typography>
                    <Typography sx={{ color: '#888', fontSize: '0.875rem', mt: 0.5 }}>
                      We typically respond within 24 hours
                    </Typography>
                  </Box>
                </Box>
                
                <Divider sx={{ mb: 4, borderColor: 'rgba(0,0,0,0.08)' }} />

                <Box component="form" onSubmit={handleSubmit} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ flex: 1 }}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Your Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: '#fafafa',
                            '&:hover': {
                              backgroundColor: '#f5f5f5',
                            },
                            '&.Mui-focused': {
                              backgroundColor: 'white',
                              '& fieldset': {
                                borderColor: 'var(--primary)',
                                borderWidth: 1.5,
                              }
                            }
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: 'var(--primary)',
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: '#fafafa',
                            '&:hover': {
                              backgroundColor: '#f5f5f5',
                            },
                            '&.Mui-focused': {
                              backgroundColor: 'white',
                              '& fieldset': {
                                borderColor: 'var(--primary)',
                                borderWidth: 1.5,
                              }
                            }
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: 'var(--primary)',
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Phone Number"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Optional"
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: '#fafafa',
                            '&:hover': {
                              backgroundColor: '#f5f5f5',
                            },
                            '&.Mui-focused': {
                              backgroundColor: 'white',
                              '& fieldset': {
                                borderColor: 'var(--primary)',
                                borderWidth: 1.5,
                              }
                            }
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: 'var(--primary)',
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: '#fafafa',
                            '&:hover': {
                              backgroundColor: '#f5f5f5',
                            },
                            '&.Mui-focused': {
                              backgroundColor: 'white',
                              '& fieldset': {
                                borderColor: 'var(--primary)',
                                borderWidth: 1.5,
                              }
                            }
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: 'var(--primary)',
                          }
                        }}
                      />
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        fullWidth
                        label="Message"
                        name="message"
                        multiline
                        rows={5}
                        value={formData.message}
                        onChange={handleChange}
                        required
                        variant="outlined"
                        placeholder="Tell us how we can help you..."
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: '#fafafa',
                            '&:hover': {
                              backgroundColor: '#f5f5f5',
                            },
                            '&.Mui-focused': {
                              backgroundColor: 'white',
                              '& fieldset': {
                                borderColor: 'var(--primary)',
                                borderWidth: 1.5,
                              }
                            }
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: 'var(--primary)',
                          }
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    endIcon={<SendIcon />}
                    sx={{
                      mt: 3,
                      py: 1.5,
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, var(--primary) 0%, var(--button-hover) 100%)',
                      fontSize: '1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      boxShadow: '0 2px 8px color-mix(in srgb, var(--primary) 20%, transparent)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, var(--button-hover) 0%, #018580 100%)',
                        boxShadow: '0 4px 16px color-mix(in srgb, var(--primary) 30%, transparent)',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Send Message
                  </Button>
                </Box>
              </Paper>
            </Zoom>
          </Grid>
          )}

          {/* Enhanced Contact Information */}
          <Grid item xs={12} lg={businessEmail ? 6 : 12} sx={{ display: 'flex' }}>
            <Box sx={{ position: { lg: 'sticky' }, top: 100, width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Fade in={true} timeout={1200}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: { xs: 3, sm: 4 },
                    background: 'linear-gradient(135deg, color-mix(in srgb, var(--primary) 90%, transparent) 0%, rgba(1,154,149,0.9) 100%)',
                    color: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 8px 25px color-mix(in srgb, var(--primary) 25%, transparent)',
                    position: 'relative',
                    overflow: 'hidden',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)',
                      pointerEvents: 'none',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, position: 'relative', zIndex: 1 }}>
                    <Box sx={{
                      p: 1.5,
                      borderRadius: '10px',
                      bgcolor: 'rgba(255,255,255,0.15)',
                      mr: 2,
                      backdropFilter: 'blur(10px)',
                    }}>
                      <BusinessIcon sx={{ fontSize: 28 }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {businessName}
                    </Typography>
                  </Box>
                  
                  {/* Location and Hours */}
                  {contactInfo.filter(info => info.title === 'Visit Our Store' || info.title === 'Working Hours').map((info, index) => (
                    <Box 
                      key={index} 
                      sx={{ 
                        mb: 3,
                        position: 'relative',
                        zIndex: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Box sx={{ 
                          p: 1.2, 
                          borderRadius: '8px', 
                          bgcolor: 'rgba(255,255,255,0.1)',
                          mr: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backdropFilter: 'blur(10px)',
                        }}>
                          {React.cloneElement(info.icon, { sx: { color: 'white' } })}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ 
                            fontWeight: 600, 
                            mb: 1, 
                            fontSize: '0.95rem', 
                            opacity: 0.95,
                            letterSpacing: '0.5px'
                          }}>
                            {info.title}
                          </Typography>
                          {info.details.map((detail, idx) => (
                            <Typography 
                              key={idx} 
                              sx={{ 
                                fontSize: '0.875rem',
                                mb: 0.5,
                                opacity: 0.9,
                                lineHeight: 1.6,
                              }}
                            >
                              {detail}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  ))}

                  {/* Phone and Email on same line */}
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 2,
                    position: 'relative',
                    zIndex: 1,
                    mb: 3
                  }}>
                    {contactInfo.filter(info => info.title === 'Phone' || info.title === 'Email').map((info, index) => (
                      <Box 
                        key={index}
                        sx={{ 
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Box sx={{ 
                          p: 1.2,
                          borderRadius: '8px', 
                          bgcolor: 'rgba(255,255,255,0.1)',
                          mr: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backdropFilter: 'blur(10px)',
                        }}>
                          {React.cloneElement(info.icon, { sx: { color: 'white' } })}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ 
                            fontWeight: 600, 
                            mb: 1, 
                            fontSize: '0.95rem', 
                            opacity: 0.95,
                            letterSpacing: '0.5px'
                          }}>
                            {info.title}
                          </Typography>
                          {info.details.map((detail, idx) => (
                            <Typography 
                              key={idx} 
                              sx={{ 
                                fontSize: '0.875rem',
                                mb: 0.5,
                                color: 'white',
                                fontWeight: 500,
                                lineHeight: 1.6,
                                ...(info.isLink && {
                                  cursor: 'pointer',
                                  textDecoration: 'none',
                                  display: 'inline-block',
                                  transition: 'all 0.2s',
                                  '&:hover': { 
                                    opacity: 0.8,
                                    transform: 'translateX(2px)',
                                  },
                                })
                              }}
                              component={info.isLink ? 'a' : 'div'}
                              href={info.isLink ? `${info.linkPrefix}${detail.replace(/\s/g, '')}` : undefined}
                            >
                              {detail}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Box>

                </Paper>
              </Fade>
            </Box>
          </Grid>
        </Grid>

        {/* Enhanced Map Section */}
        {businessAddress && (
        <Fade in={true} timeout={1400}>
          <Paper
            elevation={0}
            sx={{
              mt: { xs: 5, sm: 6 },
              p: 0,
              borderRadius: '12px',
              overflow: 'hidden',
              background: 'white',
              border: '1px solid rgba(0,0,0,0.08)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
            }}
          >
            <Box sx={{
              py: 2,
              px: 3,
              background: 'white',
              color: 'black',
              textAlign: 'center',
              borderBottom: '1px solid rgba(0,0,0,0.08)',
            }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '1.25rem' }}>
                Find Us on the Map
              </Typography>
              <Typography sx={{ mt: 0.5, fontSize: '0.875rem', color: '#666' }}>
                {businessAddress}
              </Typography>
            </Box>
            <Box sx={{ position: 'relative', width: '100%', height: { xs: 350, sm: 400, md: 450 } }}>
              <iframe
                src={`https://www.google.com/maps?q=${encodeURIComponent(businessAddress)}&output=embed`}
                width="100%"
                height="100%"
                style={{ border: 0, filter: 'contrast(1.1) saturate(1.2)' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Store location"
              />
            </Box>
          </Paper>
        </Fade>
        )}
      </Container>
    </GeometricBackground>
  );
};

export default Contact;